import * as React from "react";

import { LoadingSpinner } from "@app/shared";

import { SummaryResultNetworkType, SummaryResultType } from "./enumerations";

import { SummaryDetailsRouteMatch } from "@app/projects/network-assessment/summary/interfaces";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { RouteComponentProps } from "react-router-dom";
import { SummaryResultScope } from ".";
import { DropdownItem } from "../checkbox-dropdown";
import { Address } from "./Address";
import { BasicRecord } from "./basicRecord";
import { ByCounty } from "./ByCounty";
import { ByCountyRecord } from "./byCountyRecord";
import { ByGeocode } from "./ByGeocode";
import { BySpecialty } from "./BySpecialty";
import { ByState } from "./ByState";
import { NPI } from "./NPI";
import { Overall } from "./Overall";
import { OverallRecord } from "./overallRecord";
import { Specialty } from "./Specialty";
import { ViewSelectorItem } from "./ViewSelectorItem";
import { getViewNameFromKey, ViewSelectorKey } from "./viewSelectorKey";
import { ViewSelectorList } from "./ViewSelectorList";
import { NewActionMenu, NewActionMenuItem } from "../action-menu";

type DataSummariesProps = CountyProjectContextProps & RouteComponentProps<SummaryDetailsRouteMatch> & {
  activeScope: SummaryResultScope;
  defaultView: ViewSelectorKey;
  handleScopeChange: (newScope: DropdownItem<SummaryResultScope>) => void;
  summariesLoading: boolean;
  providers: (networkType: SummaryResultNetworkType, resultType: SummaryResultType, key: string | number) => string;

  addressRecords: BasicRecord[];
  byCountyRecords: ByCountyRecord[];
  byGeocodeRecords: BasicRecord[];
  bySpecialtyRecords: BasicRecord[];
  byStateRecords: BasicRecord[];
  npiRecords: BasicRecord[];
  overallRecords: OverallRecord[];
  specialtyRecords: BasicRecord[];
};

interface DataSummariesState {
  activeView: ViewSelectorKey;
}

class DataSummaries extends React.Component<DataSummariesProps, DataSummariesState> {
  constructor(props: DataSummariesProps) {
    super(props);

    this.state = {
      activeView: this.props.defaultView,
    };
  }

  public componentDidUpdate(prevProps: DataSummariesProps) {
    if (prevProps.match.params.resultType !== this.props.match.params.resultType) {
      this.setState({
        activeView: getViewNameFromKey(this.props.match.params.resultType) || ViewSelectorKey.Overall,
      });
    }
  }

  public render() {
    return (
      <>
        <ViewSelectorList defaultActiveKey={this.props.defaultView} onChange={this.handleViewChange}>
          {(onClick) => ([
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.Overall} selectorKey={ViewSelectorKey.Overall}><i className="fa fa-users" /> Overall</ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.BySpecialty} selectorKey={ViewSelectorKey.BySpecialty}><i className="fa fa-users" /> By Specialty</ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.ByCounty} selectorKey={ViewSelectorKey.ByCounty}><i className="fa fa-users" /> By County</ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.ByState} selectorKey={ViewSelectorKey.ByState}><i className="fa fa-users" /> By State</ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.ByGeocode} selectorKey={ViewSelectorKey.ByGeocode}><i className="fa fa-users" /> By Geocode</ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.NPI} selectorKey={ViewSelectorKey.NPI}><i className="fa fa-check" /> NPI</ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.Specialty} selectorKey={ViewSelectorKey.Specialty}><i className="fa fa-check" /> Specialty</ViewSelectorItem>,
            <ViewSelectorItem onClick={onClick} activeView={this.state.activeView} key={ViewSelectorKey.Address} selectorKey={ViewSelectorKey.Address}><i className="fa fa-check" /> Address</ViewSelectorItem>,
          ])}
        </ViewSelectorList>

        {this.renderScopeToggle()}
        {this.renderActiveView()}
      </>
    );
  }

  private renderScopeToggle(): React.ReactNode {
    const activeText = this.props.activeScope.charAt(0).toUpperCase() + this.props.activeScope.slice(1);
    const dropdownItems: Array<DropdownItem<SummaryResultScope>> = [
      {
        key: SummaryResultScope.Network,
        name: "Network Providers",
      },
      {
        key: SummaryResultScope.Servicing,
        name: "Servicing Providers",
      },
    ];

    return (
      <div className="mt-3 mb-3">
        <NewActionMenu
          buttonClassNames="qes-button-secondary"
          buttonText={`Current Scope: ${activeText} Providers`}
          key={this.props.activeScope}
          loading={this.props.summariesLoading}
          testId="data-summaries__scope-dropdown"
        >
          {dropdownItems.map((item) => {
            const isSelected = this.props.activeScope === item.key;
            return (
              <NewActionMenuItem
                display={item.name}
                isSelected={isSelected}
                key={item.key}
                onClick={() => this.props.handleScopeChange(item)}
                testId={`data-summaries__scope-option--${item.key}`}
              />
            );
          })}
        </NewActionMenu>
      </div>
    );
  }

  private renderActiveView(): React.ReactNode {
    if (this.props.summariesLoading) {
      return <LoadingSpinner />;
    }
    switch (this.state.activeView) {
      case ViewSelectorKey.Overall:
        return <Overall records={this.props.overallRecords} />;
      case ViewSelectorKey.BySpecialty:
        return <BySpecialty providersEndpointFunc={this.props.providers} records={this.props.bySpecialtyRecords} />;
      case ViewSelectorKey.ByCounty:
        return <ByCounty providersEndpointFunc={this.props.providers} records={this.props.byCountyRecords} />;
      case ViewSelectorKey.ByState:
        return <ByState providersEndpointFunc={this.props.providers} records={this.props.byStateRecords} />;
      case ViewSelectorKey.ByGeocode:
        return <ByGeocode providersEndpointFunc={this.props.providers} records={this.props.byGeocodeRecords} />;
      case ViewSelectorKey.NPI:
        return <NPI providersEndpointFunc={this.props.providers} records={this.props.npiRecords} />;
      case ViewSelectorKey.Specialty:
        return <Specialty providersEndpointFunc={this.props.providers} records={this.props.specialtyRecords} />;
      case ViewSelectorKey.Address:
        return <Address providersEndpointFunc={this.props.providers} records={this.props.addressRecords} />;
      default:
        return null;
    }
  }

  private handleViewChange = (key: ViewSelectorKey) => {
    this.setState({ activeView: key });
    const pathArr = this.props.location.pathname.split("/");
    if (this.props.match.path.includes(":resultType")) {
      pathArr.splice(-1, 1);
    }
    pathArr.push(key.replace(/ /g, ""));
    const newPath = pathArr.join("/");
    this.props.history.push(newPath);
  }
}

const WrappedDataSummaries = withCountyProjectContext(DataSummaries);

export { WrappedDataSummaries as DataSummaries };
